package top.wcpe.allincore.dsl.ir.model

import top.wcpe.allincore.dsl.ir.expr.IrType
import top.wcpe.allincore.dsl.ir.model.internal.ReadOnlyOrderedMap
import kotlin.jvm.JvmStatic

/**
 * derived 派生值 IR。
 *
 * 与 [UiState] 不同：derived 条目的值不是字面量，而是**表达式字节**
 * （ExprBlob，由编译器 ExprSerializer 序列化），运行时读回为 ExprNode，
 * 包成响应式 Computed（懒求值 + 自动多依赖追踪），通过 `derived.x` 只读访问。
 *
 * `types` 表与 [UiState] 同义：仅当 DSL 作者显式标注类型时写入，
 * 未标注字段不入表，运行时等价于 [IrType.ANY]。
 */
class UiDerived(exprs: Map<String, ByteArray>, types: Map<String, IrType>) {

    /** 派生值名 → 表达式字节（保序，不可变）。属性生成 getExprs()，Kotlin 侧以 .exprs 访问。 */
    val exprs: Map<String, ByteArray>

    /** 派生值名 → 静态类型；未显式标注类型的字段不在此表中。属性生成 getTypes()，Kotlin 侧以 .types 访问。 */
    val types: Map<String, IrType>

    init {
        requireNotNull(exprs) { "exprs" }
        requireNotNull(types) { "types" }
        // 复刻原 Java Collections.unmodifiableMap(new LinkedHashMap<>(...))：保序拷贝入参后包只读视图。
        this.exprs = ReadOnlyOrderedMap(LinkedHashMap(exprs))
        this.types = ReadOnlyOrderedMap(LinkedHashMap(types))
    }

    fun isEmpty(): Boolean = exprs.isEmpty()

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is UiDerived) return false
        if (types != other.types) return false
        if (exprs.keys != other.exprs.keys) return false
        // byte[] 需逐项内容比较
        for (e in exprs.entries) {
            if (!e.value.contentEquals(other.exprs[e.key])) {
                return false
            }
        }
        return true
    }

    // 逐位复刻原 Java：种子为 types.hashCode()，逐项 31*h + key.hashCode()、31*h + Arrays.hashCode(value)。
    override fun hashCode(): Int {
        var h = types.hashCode()
        for (e in exprs.entries) {
            h = 31 * h + e.key.hashCode()
            h = 31 * h + e.value.contentHashCode()
        }
        return h
    }

    override fun toString(): String =
        "UiDerived{names=" + exprs.keys +
            (if (types.isEmpty()) "" else ",types=" + types) + "}"

    companion object {
        private val EMPTY = UiDerived(emptyMap(), emptyMap())

        /** 返回空派生表实例。 */
        @JvmStatic
        fun empty(): UiDerived = EMPTY
    }
}
